import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import './brickBreaker.css';
import LeaderboardNameModal from '../common/leaderboardNameModal';
import {
  getPlayerNameValidationMessage,
  getSavedPlayerName,
  submitGameHighscore,
} from '../../../services/leaderboardService';
import { TouchInputAdapter, triggerHapticFeedback } from '../common/touchInputAdapter';
import {
  BrickBreakerAction,
  BrickBreakerState,
  Ball,
  Brick,
  Paddle,
  applyBrickBreakerAction,
  clampPaddleX,
  createInitialBrickBreakerState,
  resolveBrickHit,
  resolvePaddleBounce,
  stepBallAgainstBounds,
} from './brickBreakerEngine';
const audioFile = require('./brickBreakerTheme.mp3');

interface BrickBreakerGameProps {
  onClose?: () => void;
  onReload?: () => void;
  onLeaderboardUpdated?: () => void;
}

interface BrickBreakerSimulationState {
  gameState: BrickBreakerState;
  ball: Ball;
  paddle: Paddle;
  bricks: Brick[];
}

function BrickBreakerGame({ onClose, onReload, onLeaderboardUpdated }: BrickBreakerGameProps) {
  const { t } = useTranslation();
  const FIXED_STEP_MS = useMemo(() => 1000 / 60, []);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const accumulatorRef = useRef(0);
  const lastFrameTimestampRef = useRef(0);
  const audioRef = useRef(new Audio(audioFile));
  const isDraggingPaddleRef = useRef(false);
  const touchAdapterRef = useRef(new TouchInputAdapter({ emitDragEvents: true, swipeThreshold: 24 }));
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMobileControls, setShowMobileControls] = useState(false);
  const [mobileInputMode, setMobileInputMode] = useState<'drag' | 'buttons'>('drag');
  
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('brickBreakerHighScore') || '0');
  });
  const [showNameModal, setShowNameModal] = useState(false);
  const [scoreNameError, setScoreNameError] = useState<string | null>(null);
  const [isSubmittingScore, setIsSubmittingScore] = useState(false);
  const [nameDefault, setNameDefault] = useState(getSavedPlayerName());
  const leaderboardSubmittedRef = useRef(false);

  // Responsive canvas size
  const getCanvasSize = () => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const maxWidth = Math.min(windowWidth * 0.9, 800);
    const maxHeight = Math.min(windowHeight * 0.8, 600);
    return { width: maxWidth, height: maxHeight };
  };

  const [canvasSize, setCanvasSize] = useState(getCanvasSize());

  const [simulation, setSimulation] = useState<BrickBreakerSimulationState>(() => ({
    gameState: createInitialBrickBreakerState(),
    ball: {
      x: canvasSize.width / 2,
      y: canvasSize.height - 100,
      dx: 4,
      dy: -4,
      radius: 8,
    },
    paddle: {
      x: canvasSize.width / 2 - 50,
      y: canvasSize.height - 30,
      width: 100,
      height: 15,
    },
    bricks: [],
  }));

  const dispatchGameAction = useCallback((action: BrickBreakerAction) => {
    setSimulation((prev) => ({
      ...prev,
      gameState: applyBrickBreakerAction(prev.gameState, action),
    }));
  }, []);

  const gameState = simulation.gameState;
  const ball = simulation.ball;
  const paddle = simulation.paddle;
  const bricks = simulation.bricks;
  const paddleMoveIntervalRef = useRef<number | null>(null);

  // Level configurations wrapped in useMemo
  const levelConfigs = useMemo(() => [
    { rows: 3, cols: 8, ballSpeed: 4, brickTypes: [1] },
    { rows: 4, cols: 8, ballSpeed: 4.5, brickTypes: [1, 2] },
    { rows: 4, cols: 9, ballSpeed: 5, brickTypes: [1, 2] },
    { rows: 5, cols: 9, ballSpeed: 5.5, brickTypes: [1, 2, 3] },
    { rows: 5, cols: 10, ballSpeed: 6, brickTypes: [1, 2, 3] },
    { rows: 6, cols: 10, ballSpeed: 6.5, brickTypes: [1, 2, 3] },
    { rows: 6, cols: 11, ballSpeed: 7, brickTypes: [1, 2, 3, 4] },
    { rows: 7, cols: 11, ballSpeed: 7.5, brickTypes: [1, 2, 3, 4] },
    { rows: 7, cols: 12, ballSpeed: 8, brickTypes: [1, 2, 3, 4] },
    { rows: 8, cols: 12, ballSpeed: 8.5, brickTypes: [1, 2, 3, 4, 5] },
  ], []);

  const brickColors = useMemo(() => ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3'], []);

  const resetGame = useCallback(() => {
    const newCanvasSize = getCanvasSize();
    setCanvasSize(newCanvasSize);
    const config = levelConfigs[gameState.level - 1];
    const brickWidth = (newCanvasSize.width - 40) / config.cols;
    const brickHeight = 25;
    const offsetX = 20;
    const offsetY = 60;
    const newBricks: Brick[] = [];

    for (let row = 0; row < config.rows; row++) {
      for (let col = 0; col < config.cols; col++) {
        const brickType = config.brickTypes[Math.floor(Math.random() * config.brickTypes.length)];
        newBricks.push({
          x: col * brickWidth + offsetX,
          y: row * (brickHeight + 5) + offsetY,
          width: brickWidth - 2,
          height: brickHeight,
          color: brickColors[brickType - 1],
          hits: 0,
          maxHits: brickType,
          points: brickType * 10,
        });
      }
    }

    setSimulation((prev) => ({
      ...prev,
      ball: {
        x: newCanvasSize.width / 2,
        y: newCanvasSize.height - 100,
        dx: config.ballSpeed,
        dy: -config.ballSpeed,
        radius: 8,
      },
      paddle: {
        x: newCanvasSize.width / 2 - 50,
        y: newCanvasSize.height - 30,
        width: 100,
        height: 15,
      },
      bricks: newBricks,
    }));
  }, [brickColors, gameState.level, levelConfigs]);

  const startGame = () => {
    leaderboardSubmittedRef.current = false;
    setShowNameModal(false);
    setScoreNameError(null);
    accumulatorRef.current = 0;
    lastFrameTimestampRef.current = 0;
    dispatchGameAction({ type: 'START' });
    resetGame();
  };

  const simulateStep = useCallback(() => {
    if (!canvasRef.current || gameState.gameOver || gameState.gameWon || gameState.isPaused) return;

    const canvas = canvasRef.current;

    // Deterministic single-state tick.
    setSimulation((prev) => {
      let nextGameState = prev.gameState;
      let nextBall = stepBallAgainstBounds(prev.ball, canvas.width, canvas.height);
      nextBall = resolvePaddleBounce(nextBall, prev.paddle);

      if (nextBall.y > canvas.height) {
        nextGameState = applyBrickBreakerAction(nextGameState, { type: 'LOSE_LIFE' });
        nextBall = {
          ...nextBall,
          x: canvas.width / 2,
          y: canvas.height - 100,
          dy: -Math.abs(nextBall.dy),
        };
      }

      const hitResult = resolveBrickHit(nextBall, prev.bricks);
      nextBall = hitResult.ball;
      let nextBricks = hitResult.bricks;

      if (hitResult.scoreIncrease > 0) {
        nextGameState = applyBrickBreakerAction(nextGameState, {
          type: 'ADD_SCORE',
          points: hitResult.scoreIncrease,
        });
      }

      if (nextBricks.length === 0) {
        const progressedState = applyBrickBreakerAction(nextGameState, { type: 'NEXT_LEVEL' });
        const levelChanged = progressedState.level !== nextGameState.level;
        nextGameState = progressedState;

        if (levelChanged && !progressedState.gameWon) {
          const config = levelConfigs[progressedState.level - 1];
          const brickWidth = (canvas.width - 40) / config.cols;
          const brickHeight = 25;
          const offsetX = 20;
          const offsetY = 60;
          const regeneratedBricks: Brick[] = [];

          for (let row = 0; row < config.rows; row++) {
            for (let col = 0; col < config.cols; col++) {
              const brickType = config.brickTypes[Math.floor(Math.random() * config.brickTypes.length)];
              regeneratedBricks.push({
                x: col * brickWidth + offsetX,
                y: row * (brickHeight + 5) + offsetY,
                width: brickWidth - 2,
                height: brickHeight,
                color: brickColors[brickType - 1],
                hits: 0,
                maxHits: brickType,
                points: brickType * 10,
              });
            }
          }
          nextBricks = regeneratedBricks;
        }
      }

      return {
        ...prev,
        gameState: nextGameState,
        ball: nextBall,
        bricks: nextBricks,
      };
    });
  }, [brickColors, gameState.gameOver, gameState.gameWon, gameState.isPaused, levelConfigs]);

  const drawGame = useCallback((ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
    // Draw background
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#1a1a2e');
    gradient.addColorStop(1, '#16213e');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw bricks
    bricks.forEach(brick => {
      const gradient = ctx.createLinearGradient(brick.x, brick.y, brick.x, brick.y + brick.height);
      gradient.addColorStop(0, brick.color);
      gradient.addColorStop(1, brick.color + '80');
      ctx.fillStyle = gradient;
      ctx.fillRect(brick.x, brick.y, brick.width, brick.height);
      
      // Add border
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.strokeRect(brick.x, brick.y, brick.width, brick.height);
    });

    // Draw paddle
    const paddleGradient = ctx.createLinearGradient(paddle.x, paddle.y, paddle.x, paddle.y + paddle.height);
    paddleGradient.addColorStop(0, '#4ECDC4');
    paddleGradient.addColorStop(1, '#44A08D');
    ctx.fillStyle = paddleGradient;
    ctx.fillRect(paddle.x, paddle.y, paddle.width, paddle.height);

    // Draw ball
    const ballGradient = ctx.createRadialGradient(ball.x, ball.y, 0, ball.x, ball.y, ball.radius);
    ballGradient.addColorStop(0, '#FF6B6B');
    ballGradient.addColorStop(1, '#E55D87');
    ctx.fillStyle = ballGradient;
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.radius, 0, Math.PI * 2);
    ctx.fill();

    // Draw UI
    ctx.fillStyle = '#fff';
    ctx.font = '20px Arial';
    ctx.fillText(t('gamesCommon.scoreWithValue', { score: gameState.score }), 20, 30);
    ctx.fillText(t('gamesCommon.livesWithValue', { lives: gameState.lives }), 20, 55);
    ctx.fillText(t('gamesCommon.levelWithValue', { level: gameState.level }), canvas.width - 120, 30);
    ctx.fillText(t('gamesCommon.highScoreWithValue', { score: highScore }), canvas.width - 180, 55);
  }, [ball, bricks, gameState, paddle, t, highScore]);

  const drawFrame = useCallback(() => {
    if (!canvasRef.current || gameState.gameOver || gameState.gameWon || gameState.isPaused) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw everything
    drawGame(ctx, canvas);
  }, [gameState, drawGame]);

  const gameLoop = useCallback((timestamp: number) => {
    if (!canvasRef.current || gameState.gameOver || gameState.gameWon || gameState.isPaused) return;

    if (!lastFrameTimestampRef.current) {
      lastFrameTimestampRef.current = timestamp;
    }

    const delta = timestamp - lastFrameTimestampRef.current;
    lastFrameTimestampRef.current = timestamp;
    accumulatorRef.current += delta;

    while (accumulatorRef.current >= FIXED_STEP_MS) {
      simulateStep();
      accumulatorRef.current -= FIXED_STEP_MS;
    }

    drawFrame();
    animationRef.current = requestAnimationFrame(gameLoop);
  }, [FIXED_STEP_MS, drawFrame, gameState.gameOver, gameState.gameWon, gameState.isPaused, simulateStep]);

  const emitHaptic = useCallback((level: 'light' | 'medium') => {
    triggerHapticFeedback(level);
  }, []);

  const movePaddleToClientX = useCallback((clientX: number) => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = clientX - rect.left;

    setSimulation((prev) => ({
      ...prev,
      paddle: {
        ...prev.paddle,
        x: clampPaddleX(x - prev.paddle.width / 2, prev.paddle.width, canvasSize.width),
      },
    }));
  }, [canvasSize.width]);

  // Mouse movement for paddle
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (showMobileControls) {
      return;
    }
    movePaddleToClientX(e.clientX);
  };

  const handleCanvasTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!showMobileControls || mobileInputMode !== 'drag') {
      return;
    }

    isDraggingPaddleRef.current = true;
    const touch = e.touches[0];
    touchAdapterRef.current.start(touch.clientX, touch.clientY, Date.now());
    movePaddleToClientX(touch.clientX);
  }, [mobileInputMode, movePaddleToClientX, showMobileControls]);

  const handleCanvasTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    if (!showMobileControls || mobileInputMode !== 'drag' || !isDraggingPaddleRef.current) {
      return;
    }

    e.preventDefault();
    const touch = e.touches[0];
    const events = touchAdapterRef.current.move(touch.clientX, touch.clientY, Date.now());

    for (const event of events) {
      if (event.type === 'drag') {
        movePaddleToClientX(event.x);
      }
    }
  }, [mobileInputMode, movePaddleToClientX, showMobileControls]);

  const handleCanvasTouchEnd = useCallback(() => {
    isDraggingPaddleRef.current = false;
    touchAdapterRef.current.cancel();
  }, []);

  const stopPaddleMove = useCallback(() => {
    if (paddleMoveIntervalRef.current !== null) {
      window.clearInterval(paddleMoveIntervalRef.current);
      paddleMoveIntervalRef.current = null;
    }
  }, []);

  const startPaddleMove = useCallback((delta: number) => {
    if (!gameState.gameStarted || gameState.gameOver || gameState.gameWon || gameState.isPaused) return;

    setSimulation((prev) => ({
      ...prev,
      paddle: {
        ...prev.paddle,
        x: clampPaddleX(prev.paddle.x + delta, prev.paddle.width, canvasSize.width),
      },
    }));

    stopPaddleMove();
    paddleMoveIntervalRef.current = window.setInterval(() => {
      setSimulation((prev) => ({
        ...prev,
        paddle: {
          ...prev.paddle,
          x: clampPaddleX(prev.paddle.x + delta, prev.paddle.width, canvasSize.width),
        },
      }));
    }, 40);
  }, [canvasSize.width, gameState.gameOver, gameState.gameStarted, gameState.gameWon, gameState.isPaused, stopPaddleMove]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        e.preventDefault();
        dispatchGameAction({ type: 'TOGGLE_PAUSE' });
      }
      if (e.key === 'ArrowLeft') {
        setSimulation((prev) => ({
          ...prev,
          paddle: {
            ...prev.paddle,
            x: clampPaddleX(prev.paddle.x - 20, prev.paddle.width, canvasSize.width),
          },
        }));
      }
      if (e.key === 'ArrowRight') {
        setSimulation((prev) => ({
          ...prev,
          paddle: {
            ...prev.paddle,
            x: clampPaddleX(prev.paddle.x + 20, prev.paddle.width, canvasSize.width),
          },
        }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canvasSize, dispatchGameAction]);

  // Audio setup
  useEffect(() => {
    audioRef.current.loop = true;
    const audio = audioRef.current;
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    if (gameState.score > highScore) {
      setHighScore(gameState.score);
      localStorage.setItem('brickBreakerHighScore', gameState.score.toString());
    }
  }, [gameState.score, highScore]);

  // Game loop
  useEffect(() => {
    if (gameState.gameStarted && !gameState.gameOver && !gameState.gameWon && !gameState.isPaused) {
      animationRef.current = requestAnimationFrame(gameLoop);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameLoop, gameState]);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      const newSize = getCanvasSize();
      setCanvasSize(newSize);
      setShowMobileControls(window.innerWidth <= 900);
      if (gameState.gameStarted) {
        resetGame();
      }
    };

    setShowMobileControls(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [gameState.gameStarted, resetGame]);

  useEffect(() => {
    return () => {
      stopPaddleMove();
    };
  }, [stopPaddleMove]);

  useEffect(() => {
    if (!gameState.gameStarted || leaderboardSubmittedRef.current) {
      return;
    }

    const finishedRun = gameState.gameOver || gameState.gameWon;
    if (!finishedRun || gameState.score <= 0) {
      return;
    }

    setNameDefault(getSavedPlayerName());
    setScoreNameError(null);
    setShowNameModal(true);
  }, [gameState.gameOver, gameState.gameStarted, gameState.gameWon, gameState.score, onLeaderboardUpdated]);

  const handleScoreSubmit = useCallback(async (name: string) => {
    const error = getPlayerNameValidationMessage(name);
    if (error) {
      setScoreNameError(error);
      return;
    }

    setIsSubmittingScore(true);
    setScoreNameError(null);
    try {
      const submitted = await submitGameHighscore('brickbreaker', name, gameState.score);
      if (submitted) {
        leaderboardSubmittedRef.current = true;
        setShowNameModal(false);
        onLeaderboardUpdated?.();
      } else {
        setScoreNameError('leaderboardModal.errorOnlineUnavailable');
      }
    } catch (submitError) {
      setScoreNameError('leaderboardModal.errorSubmit');
      console.error('Brick Breaker leaderboard submit failed:', submitError);
    } finally {
      setIsSubmittingScore(false);
    }
  }, [gameState.score, onLeaderboardUpdated]);

  const handleScoreSkip = useCallback(() => {
    leaderboardSubmittedRef.current = true;
    setScoreNameError(null);
    setShowNameModal(false);
  }, []);

  const toggleMusic = () => {
    setIsPlaying(!isPlaying);
  };

  const restartGame = () => {
    leaderboardSubmittedRef.current = false;
    setShowNameModal(false);
    setScoreNameError(null);
    accumulatorRef.current = 0;
    lastFrameTimestampRef.current = 0;
    dispatchGameAction({ type: 'RESTART' });
    resetGame();
  };

  return (
    <div className="brick-breaker-container">
      <div className="game-header">
        <div className="controls left">
          <button onClick={toggleMusic} className="music-btn">
            {isPlaying ? '🔊' : '🔇'} {t('gamesCommon.music')}
          </button>
        </div>
        <div className="controls right">
          {onClose && (
            <button onClick={onClose} className="close-button">
              ✕
            </button>
          )}
        </div>
      </div>

      {!gameState.gameStarted ? (
        <div className="start-screen">
          <h2>{t('brickBreakerGame.title')}</h2>
          <p>{t('brickBreakerGame.startControls')}</p>
          <p>{t('brickBreakerGame.startPause')}</p>
          <p>{t('brickBreakerGame.startObjective')}</p>
          <button onClick={startGame} className="start-btn">{t('gamesCommon.startGame')}</button>
        </div>
      ) : (
        <>
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            onMouseMove={handleMouseMove}
            onTouchStart={handleCanvasTouchStart}
            onTouchMove={handleCanvasTouchMove}
            onTouchEnd={handleCanvasTouchEnd}
            onTouchCancel={handleCanvasTouchEnd}
            className="game-canvas"
          />

          {showMobileControls && (
            <div className="brick-mobile-controls">
              <button
                className="brick-mobile-btn brick-mobile-btn-pause"
                onClick={() => {
                  setMobileInputMode(prev => (prev === 'drag' ? 'buttons' : 'drag'));
                  emitHaptic('light');
                }}
              >
                {mobileInputMode === 'drag' ? 'DRAG' : 'BTN'}
              </button>
              <button
                className="brick-mobile-btn brick-mobile-btn-pause"
                onClick={() => {
                  dispatchGameAction({ type: 'TOGGLE_PAUSE' });
                  emitHaptic('light');
                }}
              >
                {gameState.isPaused ? '▶' : 'II'}
              </button>
              {mobileInputMode === 'buttons' ? (
                <>
                  <button
                    className="brick-mobile-btn"
                    onMouseDown={() => startPaddleMove(-22)}
                    onMouseUp={stopPaddleMove}
                    onMouseLeave={stopPaddleMove}
                    onTouchStart={(e) => { e.preventDefault(); startPaddleMove(-22); }}
                    onTouchEnd={stopPaddleMove}
                    onTouchCancel={stopPaddleMove}
                  >
                    ←
                  </button>
                  <button
                    className="brick-mobile-btn"
                    onMouseDown={() => startPaddleMove(22)}
                    onMouseUp={stopPaddleMove}
                    onMouseLeave={stopPaddleMove}
                    onTouchStart={(e) => { e.preventDefault(); startPaddleMove(22); }}
                    onTouchEnd={stopPaddleMove}
                    onTouchCancel={stopPaddleMove}
                  >
                    →
                  </button>
                </>
              ) : (
                <div className="brick-mobile-hint">{t('brickBreakerGame.startMobile')}</div>
              )}
            </div>
          )}
          
          {gameState.isPaused && (
            <div className="pause-overlay">
              <h2>{t('gamesCommon.paused')}</h2>
              <p>{t('brickBreakerGame.pressSpaceToContinue')}</p>
            </div>
          )}
          
          {gameState.gameOver && (
            <div className="game-over-overlay">
              <h2>{t('gamesCommon.gameOver')}</h2>
              <p>{t('gamesCommon.finalScore', { score: gameState.score })}</p>
              {gameState.score === highScore && <p className="new-high-score">{t('gamesCommon.newHighScore')}</p>}
              <button onClick={restartGame} className="restart-btn">{t('gamesCommon.playAgain')}</button>
            </div>
          )}
          
          {gameState.gameWon && (
            <div className="game-won-overlay">
              <h2>{t('gamesCommon.congratulations')}</h2>
              <p>{t('brickBreakerGame.completedAllLevels')}</p>
              <p>{t('gamesCommon.finalScore', { score: gameState.score })}</p>
              {gameState.score === highScore && <p className="new-high-score">{t('gamesCommon.newHighScore')}</p>}
              <button onClick={restartGame} className="restart-btn">{t('gamesCommon.playAgain')}</button>
            </div>
          )}
        </>
      )}
      <LeaderboardNameModal
        open={showNameModal}
        score={gameState.score}
        defaultName={nameDefault}
        isSubmitting={isSubmittingScore}
        error={scoreNameError}
        onSubmit={handleScoreSubmit}
        onCancel={handleScoreSkip}
      />
    </div>
  );
}

export default BrickBreakerGame;